import React, { useContext, useState } from "react";
import { useHistory, withRouter } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import { useTheme } from "styled-components";

import {
  Cart,
  Container,
  Inner,
  Logo,
  MobileView,
  MobileFilterBar,
  MobileFilterBarSpace,
  SearchContainer,
  TeamLogo,
  TeamName
} from "./styles";
import Icon from "../Icon";
import { Counter } from "../Counter";
import { Button } from "../Buttons/Button";

import { SearchContext } from "../../context";
import { AuthContext } from "../../context/AuthProvider";
import { CartContext } from "../../context";
import {
  useNavBlock,
  NAV_BLOCKED_STYLE,
  NAV_BLOCKED_TITLE
} from "../../context/NavBlockContext";

import Searchbar from "../SearchInput";
import CartDropdown from "../CartDropdown";
import Aggregate from "../Aggregate";
import SearchFilters from "../SearchFilters";
import Menu from "../Menu";
import Popover from "../Popover";
import Notification from "../Notification";
import Drawer from "../../components/Drawer";

const Header = props => {
  const theme = useTheme();
  const context = useContext(SearchContext);
  const { cartItems } = useContext(CartContext);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isSearchBarOpen, toggleSearchBar] = useState(false);
  const auth = useContext(AuthContext);
  const isMobileScreen = useMediaQuery({
    query: "(max-width: 800px)"
  });
  const history = useHistory();
  const { isNavBlocked } = useNavBlock();

  // When the user is in the middle of a gated flow (subscription checkout or
  // the post-checkout ProjectSetupModal), the logo and Subscribe CTA should
  // not silently navigate away — that's how users were losing their setup
  // modal mid-flow.
  const goHomeIfAllowed = () => {
    if (isNavBlocked) return;
    history.push("/");
  };
  const navBlockedStyle = isNavBlocked ? NAV_BLOCKED_STYLE : undefined;
  const navBlockedTitle = isNavBlocked ? NAV_BLOCKED_TITLE : undefined;

  const team = auth.user.team;

  const showAggregate = () => {
    const isOnPlaylistScreen = props.location.pathname.includes("/playlist/");
    const isOnAlbumScreen = props.location.pathname.includes("/album");
    const isOnTrackScreen = props.location.pathname.includes("/browse/track");
    const hasSelectedTracks = context.selectedTrackList;

    if (!hasSelectedTracks) return false;

    return isOnPlaylistScreen || isOnAlbumScreen || isOnTrackScreen;
  };

  const showSearchComponents = () =>
    props.location.pathname.includes("/browse") ||
    props.location.pathname === "/";

  // The filter drawer (and its applied-filter chips) only exist on the Browse
  // page, where SidePanel is mounted.
  const showFilterTrigger = () => props.location.pathname.includes("/browse");

  const hasActiveFilters =
    (context.searchFilters || []).some(f => !f.hidden && !f.aggregate) ||
    (context.selectedAggregates || []).length > 0;

  const showCart = () => {
    const isNotCheckoutScreen = props.location.pathname !== "/order";
    const isNotOrderScreen =
      !props.location.pathname.includes("/subscription/") &&
      !props.location.pathname.includes("/order-confirmation");

    const hasCartItems = cartItems.length > 0;

    return isNotCheckoutScreen && isNotOrderScreen && hasCartItems;
  };

  const showSubscribeCTA = () => {
    if (isMobileScreen) return false;
    if (auth.user.role === "guest") return true;
    if (auth.user.role !== "private") return false;

    // Gate on p.subscription (backend filters to active Paid/Offline subs), NOT
    // project.downloadAccess — that flag stays true through the post-cancellation grace
    // period, which wrongly kept the Subscribe CTA hidden for users who had canceled.
    const hasBlanketAccess =
      auth.user.fullDownloadAccess ||
      (auth.user.team && auth.user.team.enterprise) ||
      (auth.user.projects || []).some(
        p => p.subscription || (p.team && p.team.enterprise)
      );

    return !hasBlanketAccess;
  };

  const showProfile = () => auth.isUserPrivate();

  const showNotification = () => auth.isUserPrivate();

  const cartIcon = (
    <Cart onClick={() => setIsCartOpen(prev => !prev)}>
      <Icon
        type="cart"
        size="md"
        color={cartItems.length > 0 ? theme.primary : undefined}
        label="cart"
      />
      {cartItems.length > 0 && (
        <Counter>
          <p className="p4b text">{cartItems.length}</p>
        </Counter>
      )}
    </Cart>
  );

  const showTeamBranding = Boolean(auth.user.customLogo && team);

  // show team image only when team image is set (default image starts wiht
  // '/image/Defaults/art')
  const teamImageSrc =
    team && team.image && team.image.startsWith("/image/team/")
      ? `${process.env.REACT_APP_FILES_URL}${team.image}`
      : null;

  return (
    <Container>
      {!isMobileScreen && (
        <Inner customLogo={showTeamBranding}>
          {showTeamBranding ? (
            <div
              className="header-container"
              style={navBlockedStyle}
              title={navBlockedTitle}
            >
              {teamImageSrc ? (
                <div className="team-row" onClick={goHomeIfAllowed}>
                  <TeamLogo src={teamImageSrc} alt={team.name} />
                  <div className="team-text">
                    <p className="team-name">{team.name}</p>
                    <p className="team-subtitle ws-nowrap">Blue Dot Sessions</p>
                  </div>
                </div>
              ) : (
                <>
                  <TeamName $color={team.color} onClick={goHomeIfAllowed}>
                    <p className="team-title ws-nowrap">{team.name}</p>
                  </TeamName>
                  <div className="blue-dot-title">
                    <p className="p4b text">Blue Dot Sessions</p>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div
              className="logo-container"
              style={navBlockedStyle}
              title={navBlockedTitle}
            >
              <Logo
                onClick={goHomeIfAllowed}
                src={require("../../assets/logo.svg")}
                alt="blue-dot"
              />
            </div>
          )}

          <div className="always-align-top">
            <SearchContainer>
              {showSearchComponents() && (
                <React.Fragment>
                  <span
                    style={{
                      width: "fit-content",
                      marginRight: 24
                    }}
                  >
                    <Searchbar />
                  </span>
                </React.Fragment>
              )}
              {showAggregate() && (
                <span>
                  <Aggregate />
                </span>
              )}

              {showSearchComponents() && (
                <span style={{ width: "100%" }}>
                  <SearchFilters />
                </span>
              )}
            </SearchContainer>

            {showSubscribeCTA() && (
              <span style={navBlockedStyle} title={navBlockedTitle}>
                <Button
                  onClick={() => {
                    if (isNavBlocked) return;
                    history.push("/subscription");
                  }}
                  disabled={isNavBlocked}
                  subscribe={true}
                  className="button ws-nowrap"
                  height="24"
                >
                  Subscribe
                </Button>
              </span>
            )}
            {showCart() && (
              <Popover
                right="-10px"
                top="44px"
                triggerComponent={cartIcon}
                dropdown={<CartDropdown />}
                showDropdown={isCartOpen}
                onClose={() => setIsCartOpen(false)}
              />
            )}
            {showNotification() && <Notification />}
            <Menu
              onMenuClick={() => setIsDrawerOpen(!isDrawerOpen)}
              isDrawerOpen={isDrawerOpen}
              showProfile={showProfile}
            />
          </div>
        </Inner>
      )}

      {isMobileScreen && (
        <MobileView>
          {!isSearchBarOpen && (
            <React.Fragment>
              {showTeamBranding && teamImageSrc ? (
                <TeamLogo
                  $size="36px"
                  onClick={goHomeIfAllowed}
                  src={teamImageSrc}
                  alt={team.name}
                  style={navBlockedStyle}
                  title={navBlockedTitle}
                />
              ) : (
                <Logo
                  onClick={goHomeIfAllowed}
                  src={require("../../assets/logo.svg")}
                  alt="blue dot sessions logo"
                  style={navBlockedStyle}
                  title={navBlockedTitle}
                />
              )}
              <div className="row mobile-items">
                {showSearchComponents() && (
                  <div className="search-icon">
                    <Icon
                      type="search"
                      size="md"
                      label="blue-dot-search"
                      onClick={() => toggleSearchBar(true)}
                    />
                  </div>
                )}
                {showFilterTrigger() && (
                  <div className="filter-icon">
                    <Icon
                      type="quick_filter"
                      size="md"
                      label="open-filters"
                      color={hasActiveFilters ? theme.primary : undefined}
                      onClick={() =>
                        context.filterDrawerOpen
                          ? context.closeFilterDrawer()
                          : context.openFilterDrawer()
                      }
                    />
                  </div>
                )}
                {showCart() && (
                  <Popover
                    right="-10px"
                    top="44px"
                    triggerComponent={cartIcon}
                    dropdown={<CartDropdown />}
                    showDropdown={isCartOpen}
                    onClose={() => setIsCartOpen(false)}
                  />
                )}
                <Menu
                  onMenuClick={() => setIsDrawerOpen(!isDrawerOpen)}
                  isDrawerOpen={isDrawerOpen}
                />
              </div>
            </React.Fragment>
          )}
          {isSearchBarOpen && (
            <Searchbar toggleSearchBar={toggleSearchBar} mobile={true} />
          )}

          {!isSearchBarOpen && showFilterTrigger() && hasActiveFilters && (
            <>
              <MobileFilterBarSpace />
              <MobileFilterBar>
                <SearchFilters mobileView static />
              </MobileFilterBar>
            </>
          )}

          <div className="drawer-container">
            {isDrawerOpen && (
              <Drawer closeDrawer={() => setIsDrawerOpen(false)} />
            )}
          </div>
        </MobileView>
      )}
    </Container>
  );
};

export default withRouter(Header);
